import { useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { kVolleyballScoringPrimer } from "../utils/aiProviders";
import {
    buildDeepAnalysisFromSections,
    buildMatchEventsCSV,
    deepAnalysisSection,
    downloadCSV,
    kDeepAnalysisSections,
    matchCSVFileName,
} from "../utils/MatchCSV";
import AIReportPanel from "./AIReportPanel";

const kQuestionsStorage = "VBLiveDeepAnalysisQuestions";

// Special selector value that runs every section at once (uses the most AI
// credits). Individual sections are the default to keep each request small.
const kAllSectionsKey = "__all__";

// Loads any user-edited questions from localStorage, keyed by section.
function loadStoredQuestions() {
  try {
    const raw = localStorage.getItem(kQuestionsStorage);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

// The starting text (one question per line) for a section, translated into the
// app's current language via the provided translate function.
function defaultText(key, t) {
  const section = deepAnalysisSection(key);
  return section ? section.questions.map((q) => t(q)).join("\n") : "";
}

// Splits an edited textarea value into a list of trimmed questions.
const linesOf = (text) =>
  (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

// Lets the user run the deep AI analysis one section at a time (Serving,
// Reception, Setting, …) so each request stays small and cheap, and edit or add
// to the questions for each section before submitting.
function DeepAnalysisPanel({
  matches,
  selectedGame,
  selectedTeam,
  selectedTeamLabel,
  canGenerate,
}) {
  const { t } = useLanguage();
  const [sectionKey, setSectionKey] = useState(kDeepAnalysisSections[0].key);
  // Map of section key -> edited textarea text. Initialised from defaults,
  // overlaid with any stored edits.
  const [questions, setQuestions] = useState(() => {
    const stored = loadStoredQuestions();
    const init = {};
    kDeepAnalysisSections.forEach((s) => {
      init[s.key] =
        typeof stored[s.key] === "string"
          ? stored[s.key]
          : defaultText(s.key, t);
    });
    return init;
  });

  const persist = (next) => {
    setQuestions(next);
    try {
      localStorage.setItem(kQuestionsStorage, JSON.stringify(next));
    } catch (e) {
      // ignore storage failures (e.g. private mode)
    }
  };

  const onQuestionsChange = (value) => {
    persist({ ...questions, [sectionKey]: value });
  };

  const resetSection = () => {
    persist({ ...questions, [sectionKey]: defaultText(sectionKey, t) });
  };

  const sectionsForPrompt = () => {
    if (sectionKey === kAllSectionsKey) {
      return kDeepAnalysisSections.map((s) => ({
        key: s.key,
        title: s.title,
        questions: linesOf(questions[s.key]),
      }));
    }
    const section = deepAnalysisSection(sectionKey);
    return [
      {
        key: section.key,
        title: section.title,
        questions: linesOf(questions[sectionKey]),
      },
    ];
  };

  const buildPrompt = () =>
    buildDeepAnalysisFromSections(
      matches,
      selectedGame,
      selectedTeam,
      sectionsForPrompt()
    );

  const handleDownloadCSV = () => {
    const { csv, rowCount } = buildMatchEventsCSV(matches, selectedGame);
    if (!csv || rowCount === 0) {
      return;
    }
    downloadCSV(csv, matchCSVFileName(matches));
  };

  const isAll = sectionKey === kAllSectionsKey;
  const currentSection = deepAnalysisSection(sectionKey);
  const sectionTitle = isAll
    ? t("All sections")
    : t(currentSection ? currentSection.title : "");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-base-content/70">
          {t(
            "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider."
          )}
        </p>
        <button
          className="btn btn-sm btn-outline"
          onClick={handleDownloadCSV}
          disabled={!canGenerate}
        >
          {t("Download CSV")}
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-base-content/70">
          {t("Analysis section")} ({t("analysing")} {selectedTeamLabel})
        </label>
        <select
          className="select select-sm select-bordered w-full max-w-xs"
          value={sectionKey}
          onChange={(e) => setSectionKey(e.target.value)}
        >
          {kDeepAnalysisSections.map((s) => (
            <option key={s.key} value={s.key}>
              {t(s.title)}
            </option>
          ))}
          <option value={kAllSectionsKey}>
            {t("All sections (uses the most credits)")}
          </option>
        </select>
      </div>

      {!isAll && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-base-content/70">
              {t("Questions")} — {t("one per line, edit or add your own")}
            </label>
            <button
              className="btn btn-ghost btn-xs"
              onClick={resetSection}
              type="button"
            >
              {t("Reset to defaults")}
            </button>
          </div>
          <textarea
            className="textarea textarea-bordered w-full text-sm leading-relaxed"
            rows={Math.max(4, linesOf(questions[sectionKey]).length + 1)}
            value={questions[sectionKey]}
            onChange={(e) => onQuestionsChange(e.target.value)}
            placeholder={t("Add a question…")}
          />
        </div>
      )}

      {isAll && (
        <p className="text-[11px] text-base-content/50">
          {t(
            "Runs every section using the questions you've set for each. This sends one large request and uses the most credits."
          )}
        </p>
      )}

      <AIReportPanel
        key={sectionKey}
        title={`${t("AI Deep Analysis")} — ${sectionTitle}`}
        documentTitle={`Deep Analysis - ${
          isAll ? "All sections" : currentSection ? currentSection.title : ""
        }`}
        generateLabel={
          isAll ? "Generate full analysis" : "Generate section analysis"
        }
        systemPrompt={
          "You are an expert volleyball performance analyst and scout. You produce clear, practical, evidence-based analysis from ball-by-ball event data. " +
          kVolleyballScoringPrimer
        }
        buildPrompt={buildPrompt}
        canGenerate={canGenerate}
        notReadyMessage="Load at least one match with coded events to generate the analysis."
        followUpStorageKey={`VBLiveFollowUpQuestions_deep_${sectionKey}`}
      />
    </div>
  );
}

export default DeepAnalysisPanel;
